/* The in-app alert pop-up stacks, mounted once in the app shell. Each alert carries the pop-up
 * setting that applies to it (its rule's setting, else the Default — Notification Center →
 * Pop-up Settings), so alerts from different rules can sit at different screen positions: one
 * stack per position. At most MAX_VISIBLE cards show in total, the rest as "+N more" (opens the
 * bell). Not modal — it never blocks work.
 *  • Auto: a card closes after its setting's N seconds (paused while the mouse is over it). The
 *    alert stays unread in the bell.
 *  • Acknowledge (its setting says "stay until Acknowledge", or its rule is ACK REQUIRED): the
 *    card stays until Acknowledge is clicked. */
import { useEffect, useState } from 'react';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { useInboxStore } from './inboxStore';
import { useAlertPopupSettings, needsAck, effectivePopup, type AlertPopupSettings } from './alertPopupSettings';
import type { InAppAlert } from './inboxApi';
import { SeverityChip, AckChip } from './AlertChips';
import { AlertDetailDialog } from './AlertDetailDialog';
import { useAcknowledgeAlert } from './useAcknowledgeAlert';
import './inbox.css';

export const MAX_VISIBLE = 3;

function AlertCard({ alert, settings }: { alert: InAppAlert; settings: AlertPopupSettings }) {
  const dismiss = useInboxStore((s) => s.dismissPopup);
  const openDetail = useInboxStore((s) => s.openDetail);
  const acknowledge = useAcknowledgeAlert();
  const mustAck = needsAck(alert, settings);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (mustAck || hover) return;
    const t = setTimeout(() => dismiss(alert.id), settings.seconds * 1000);
    return () => clearTimeout(t);
  }, [mustAck, hover, settings.seconds, alert.id, dismiss]);

  const view = () => {
    if (alert.preview) { dismiss(alert.id); return; }   // the settings-page sample has no detail
    openDetail(alert.id);
    if (!mustAck) dismiss(alert.id);   // an ack card stays until it is acknowledged
  };
  const ack = () => {
    if (alert.preview) { dismiss(alert.id); return; }
    void acknowledge(alert.id);
  };

  return (
    <div className={`ia-card ${alert.severity ?? ''}`} role="alert" aria-live="assertive"
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="ia-card-top">
        <i className="ti ti-bell-ringing" aria-hidden="true" />
        <SeverityChip severity={alert.severity} />
        <AckChip required={mustAck} ackAt={alert.ackAt} resolvedAt={alert.resolvedAt} />
        {!mustAck && (
          <button type="button" className="ia-card-x" title="Close" aria-label="Close" onClick={() => dismiss(alert.id)}>×</button>
        )}
      </div>
      <div className="ia-card-subj">{alert.subject}</div>
      {alert.summary && <div className="ia-card-sum">{alert.summary}</div>}
      <div className="ia-card-meta">{alert.ruleName} · {fmtLocalDateTime(alert.createdAt)}</div>
      <div className="ia-card-actions">
        <button type="button" className="ca-btn" onClick={view}><i className="ti ti-eye" /> View</button>
        {mustAck && (
          <button type="button" className="ca-btn ca-btn-solid" onClick={ack}>
            <i className="ti ti-circle-check" /> Acknowledge
          </button>
        )}
      </div>
    </div>
  );
}

export function AlertPopupHost() {
  const popups = useInboxStore((s) => s.popups);
  const setBellOpen = useInboxStore((s) => s.setBellOpen);
  const fallback = useAlertPopupSettings();
  // Only the newest MAX_VISIBLE show, whatever their position.
  const visible = popups.slice(-MAX_VISIBLE);
  const hidden = popups.length - visible.length;

  // One stack per screen position, in first-seen order. "+N more" rides on the newest card's stack.
  const stacks = new Map<string, InAppAlert[]>();
  for (const a of visible) {
    const pos = effectivePopup(a, fallback).position;
    stacks.set(pos, [...(stacks.get(pos) ?? []), a]);
  }
  const newest = visible.length > 0 ? effectivePopup(visible[visible.length - 1]!, fallback).position : '';

  return (
    <>
      {[...stacks.entries()].map(([position, list]) => {
        // Newest on top for a stack anchored at the top, newest at the bottom for one anchored below.
        const ordered = position.startsWith('bottom') ? list : [...list].reverse();
        return (
          <div key={position} className={`ia-stack ${position}`} data-testid="alert-popup-stack">
            {ordered.map((a) => <AlertCard key={a.id} alert={a} settings={effectivePopup(a, fallback)} />)}
            {hidden > 0 && position === newest && (
              <button type="button" className="ia-more" onClick={() => setBellOpen(true)}>+{hidden} more</button>
            )}
          </div>
        );
      })}
      <AlertDetailDialog />
    </>
  );
}
